import { useQuery, useQueryClient } from "@tanstack/react-query";
import { DataError } from "../../core/errors/DataError";
import { Resource } from "../../core/models/wrappers/Resource";
import { useApiClient } from "../clients/ApiClient";
import {
  SendStockStatusReportNowResultDto,
  StockStatusReportDto,
  StockStatusReportPreviewDto,
  UpdateStockStatusReportDto,
} from "../models/responses/StockStatusReportDto";
import { adminSwrOptions } from "./adminSwr";

// The admin "Stock status report" page (flag
// REACT_APP_STOCK_STATUS_REPORT_ADMIN_ENABLED; API app setting
// StockStatusReportEnabled). Call budget: ONE GET ops/stock-status-report
// per visit, ONE GET .../preview per Preview click, ONE PUT per Save, ONE
// POST .../send-now per confirmed test send. Nothing polls and nothing
// retries on its own.

export interface StockStatusReportRepository {
  /**
   * The stored list (or the built-in default while nothing is saved). A 404
   * (the API's flag off) stays an error - the page tells it from an outage
   * with isStockStatusReportNotActive.
   */
  useReport(): Resource<StockStatusReportDto>;
  /**
   * Saves the WHOLE list from the revision the page holds. Resolves with
   * the new server state (already written into the query cache). Somebody
   * else saved in between = the API wrote nothing and this rejects with
   * StockStatusReportConflictError; the state the API answered with is
   * already in the query cache then.
   */
  update(body: UpdateStockStatusReportDto): Promise<StockStatusReportDto>;
  /** The SAVED list measured right now (no mail, nothing written). */
  preview(): Promise<StockStatusReportPreviewDto>;
  /** Sends the report now to exactly these recipients (a manual send). */
  sendNow(recipients: string[]): Promise<SendStockStatusReportNowResultDto>;
}

export const STOCK_STATUS_REPORT_KEY = "stock-status-report";

/** ONLY the API's 404 (its flag off) means "not active"; anything else is an error. */
export function isStockStatusReportNotActive(error: unknown): boolean {
  return error instanceof DataError && error.errorStatus?.code === 404;
}

/** The PUT's 409: the save came from a stale page (nothing was written). */
export function isStockStatusReportConflict(error: unknown): boolean {
  return error instanceof DataError && error.errorStatus?.code === 409;
}

// The 409's `current` is only trusted when it has the GET's shape.
function asReportState(value: unknown): StockStatusReportDto | null {
  if (value === null || typeof value !== "object") return null;
  const candidate = value as Partial<StockStatusReportDto>;
  if (!Array.isArray(candidate.items)) return null;
  if (!Array.isArray(candidate.recipients)) return null;
  if (typeof candidate.revision !== "number") return null;
  return candidate as StockStatusReportDto;
}

/**
 * Nothing was saved: the list was changed by somebody else after the page
 * was loaded. `message` is the API's own text; `state` is the stored state
 * as the query cache holds it NOW (the very object the page's query returns
 * next), or null when it could not be loaded.
 */
export class StockStatusReportConflictError extends Error {
  state: StockStatusReportDto | null;
  constructor(message: string, state: StockStatusReportDto | null) {
    super(message);
    this.name = "StockStatusReportConflictError";
    this.state = state;
  }
}

export function useStockStatusReportRepository(): StockStatusReportRepository {
  const apiClient = useApiClient();
  const queryClient = useQueryClient();

  function useReport(): Resource<StockStatusReportDto> {
    return useQuery(
      [STOCK_STATUS_REPORT_KEY],
      () => apiClient.getStockStatusReport(),
      {
        // Admin surface — always show the current server state on entry.
        staleTime: 0,
        // Refresh is the retry; the default 3 retries would turn one failed
        // visit (or the 404 of a flag that is off) into 4 GETs.
        retry: false,
        // Admin perf FE-3 (flag): paint from cache + one background refetch.
        ...adminSwrOptions(),
      },
    );
  }

  async function update(
    body: UpdateStockStatusReportDto,
  ): Promise<StockStatusReportDto> {
    let saved: StockStatusReportDto;
    try {
      saved = await apiClient.updateStockStatusReport(body);
    } catch (error) {
      if (!isStockStatusReportConflict(error)) throw error;
      // 409: somebody else saved after this page was loaded - nothing was
      // written. The answer carries the stored state (GET shape, with the
      // revision the next save must send): it becomes the page's state with
      // no second GET. Only an answer without a usable state costs one GET.
      const state = asReportState(
        (error as DataError).errorStatus?.stockStatusReportCurrent,
      );
      if (state) {
        queryClient.setQueryData([STOCK_STATUS_REPORT_KEY], state);
      } else {
        await queryClient.invalidateQueries({
          queryKey: [STOCK_STATUS_REPORT_KEY],
        });
      }
      throw new StockStatusReportConflictError(
        (error as DataError).message,
        queryClient.getQueryData<StockStatusReportDto>([
          STOCK_STATUS_REPORT_KEY,
        ]) ?? null,
      );
    }
    // The PUT answers with the new server state (same shape as the GET), so
    // the page follows without a second GET.
    queryClient.setQueryData([STOCK_STATUS_REPORT_KEY], saved);
    return saved;
  }

  function preview(): Promise<StockStatusReportPreviewDto> {
    return apiClient.getStockStatusReportPreview();
  }

  function sendNow(
    recipients: string[],
  ): Promise<SendStockStatusReportNowResultDto> {
    // dryRun: false - the dialog's confirm button is the explicit "send".
    return apiClient.sendStockStatusReportNow({ recipients, dryRun: false });
  }

  return { useReport, update, preview, sendNow };
}
